// Central state management for filters and pagination
// Handles all format conversions between URL params, form data, and internal state
export default class FilterState {
  constructor(initialFilters = {}) {
    // Internal state as Map for efficient operations
    this.filters = new Map()
    this.page = 1  // Page is a single value, not an array
    
    // Initialize from object (e.g., from server or URL)
    Object.entries(initialFilters).forEach(([key, values]) => {
      if (key === 'page') {
        this.setPage(values)
      } else {
        this.setFilter(key, Array.isArray(values) ? values : [values].filter(Boolean))
      }
    })
  }
  
  // Create FilterState from URL search params
  static fromUrlParams(searchParams) {
    const state = new FilterState()
    for (const [key, value] of searchParams.entries()) {
      if (value) {
        if (key === 'page') {
          state.setPage(value)
        } else {
          const values = value.split(',').map(v => v.trim()).filter(Boolean)
          state.setFilter(key, values)
        }
      }
    }
    return state
  }
  
  // Create FilterState from form data
  static fromFormData(formData) {
    const state = new FilterState()
    const grouped = {}
    
    // Group form data by field name (removing [] suffix)
    for (const [key, value] of formData.entries()) {
      const cleanKey = key.replace('[]', '')
      if (!grouped[cleanKey]) grouped[cleanKey] = []
      if (value) grouped[cleanKey].push(value)
    }
    
    // Set filters from grouped data
    Object.entries(grouped).forEach(([key, values]) => {
      if (key === 'page') {
        state.setPage(values[0]) // Page is always single value
      } else {
        state.setFilter(key, values)
      }
    })
    
    return state
  }
  
  // Set filter values for a given type
  setFilter(type, values) {
    const cleanValues = Array.isArray(values) 
      ? values.filter(v => v && v.trim())
      : (values ? [values.trim()] : [])
    
    if (cleanValues.length > 0) {
      this.filters.set(type, cleanValues)
    } else {
      this.filters.delete(type)
    }
  }
  
  // Get filter values for a given type
  getFilter(type) {
    return this.filters.get(type) || []
  }
  
  // Check if a filter has any values
  hasFilter(type) {
    return this.filters.has(type) && this.filters.get(type).length > 0
  }
  
  // Clear a specific filter
  clearFilter(type) {
    this.filters.delete(type)
  }
  
  // Set page number
  setPage(page) {
    const pageNum = parseInt(page, 10)
    this.page = (pageNum > 0) ? pageNum : 1
  }
  
  // Get current page number
  getPage() {
    return this.page
  }
  
  // Reset page to 1 (used when filters change)
  resetPage() {
    this.page = 1
  }
  
  // Clear all filters
  clearAll() {
    this.filters.clear()
    this.page = 1
  }
  
  // Check if any filters are active
  hasAnyFilters() {
    return this.filters.size > 0
  }
  
  // Check if any non-page filters are active (for reset button logic)
  hasAnyNonPageFilters() {
    return this.filters.size > 0
  }
  
  // Convert to URL search params (comma-delimited format for clean URLs)
  toUrlParams() {
    const params = new URLSearchParams()
    for (const [key, values] of this.filters.entries()) {
      if (values.length > 0) {
        params.set(key, values.join(','))
      }
    }
    // Add page parameter if not page 1
    if (this.page > 1) {
      params.set('page', this.page.toString())
    }
    return params.toString()
  }
  
  // Convert to form data (Rails array format for form submission)
  toFormData() {
    const formData = new FormData()
    for (const [key, values] of this.filters.entries()) {
      values.forEach(value => formData.append(`${key}[]`, value))
    }
    // Add page parameter if not page 1
    if (this.page > 1) {
      formData.append('page', this.page.toString())
    }
    return formData
  }
  
  // Convert to plain object for serialization/events
  toObject() {
    const obj = {}
    for (const [key, values] of this.filters.entries()) {
      obj[key] = [...values] // Create copy of array
    }
    // Include page if not page 1
    if (this.page > 1) {
      obj.page = this.page
    }
    return obj
  }
  
  // Create a copy of this state
  clone() {
    return new FilterState(this.toObject())
  }
  
  // Check if this state equals another state
  equals(other) {
    if (!(other instanceof FilterState)) return false
    if (this.filters.size !== other.filters.size) return false
    if (this.page !== other.page) return false
    
    for (const [key, values] of this.filters.entries()) {
      const otherValues = other.filters.get(key)
      if (!otherValues) return false
      if (values.length !== otherValues.length) return false
      if (!values.every(v => otherValues.includes(v))) return false
    }
    
    return true
  }
  
  // Get all filter types that have values
  getActiveFilterTypes() {
    return Array.from(this.filters.keys())
  }
  
  // Get total count of active filters
  getActiveFilterCount() {
    return Array.from(this.filters.values()).reduce((sum, values) => sum + values.length, 0)
  }
}